import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Background,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  Position,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import { Button, Form } from "react-bootstrap";
import styles from "./StructureMapFlowEditor.module.css";

type JsonRecord = Record<string, any>;
type MappingPart = "source" | "target";

interface ElementRow {
  id: string;
  partIndex: number;
  rulePath: number[];
  ruleName: string;
  name: string;
  depth: number;
  hasChildren: boolean;
  isCollapsed: boolean;
  fragment: JsonRecord;
}
interface MappingBoxData extends JsonRecord {
  part: MappingPart;
  title: string;
  contexts: string[];
  rows: ElementRow[];
  onUpdate: (part: MappingPart, row: ElementRow, patch: JsonRecord) => void;
  onRenameRule: (path: number[], name: string) => void;
  onAddTarget: (path: number[]) => void;
  onAddChildRule: (path: number[]) => void;
  onDeleteRule: (path: number[]) => void;
  onToggleRule: (path: number[]) => void;
}
interface TransformData extends JsonRecord {
  target: ElementRow;
  onUpdate: (row: ElementRow, patch: JsonRecord) => void;
  onDeleteTarget: (row: ElementRow) => void;
}
type MappingBoxNode = Node<MappingBoxData, "mappingBox">;
type TransformNode = Node<TransformData, "transform">;

const transforms = [
  "create",
  "copy",
  "truncate",
  "escape",
  "cast",
  "append",
  "translate",
  "reference",
  "dateOp",
  "uuid",
  "pointer",
  "evaluate",
  "cc",
  "c",
  "qty",
  "id",
  "cp",
];
const parameterTypes = [
  { key: "valueId", label: "id" },
  { key: "valueString", label: "string" },
  { key: "valueBoolean", label: "boolean" },
  { key: "valueInteger", label: "integer" },
  { key: "valueDecimal", label: "decimal" },
];
const asArray = (value: unknown): JsonRecord[] => (Array.isArray(value) ? (value as JsonRecord[]) : []);
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const pathKey = (path: number[]) => path.join(".");
const isDescendantOf = (path: number[], parent: number[]) => parent.every((item, index) => path[index] === item);

function getParameterValue(parameter?: JsonRecord): { key: string; value: string } {
  const key = parameter && Object.keys(parameter).find((field) => field.startsWith("value"));
  return { key: key ?? "", value: key && parameter?.[key] !== undefined ? String(parameter[key]) : "" };
}

function parameterValue(key: string, value: string): string | number | boolean {
  if (value === "") return "";
  if (key === "valueBoolean") return value === "true";
  if (key === "valueInteger" || key === "valueDecimal") return Number(value);
  return value;
}

function MappingBox({ data }: NodeProps<MappingBoxNode>) {
  const { t } = useTranslation();
  const isSource = data.part === "source";
  const update = (row: ElementRow, field: string, value: string) => data.onUpdate(data.part, row, { [field]: value });
  const label = useCallback(
    (key: string, defaultValue: string, options?: JsonRecord) => t(key, { defaultValue, ...options }),
    [t],
  );

  return (
    <div className={[styles.mappingBox, isSource ? styles.sourceBox : styles.targetBox].join(" ")}>
      <div className={styles.boxHeading}>
        <strong>
          {isSource ? label("structureMap.flow.sources", "Sources") : label("structureMap.flow.targets", "Targets")}
        </strong>
        <span>{data.title}</span>
      </div>
      <div className={styles.contexts}>
        {label("structureMap.flow.contexts", "Contexts")} :{" "}
        {data.contexts.length ? data.contexts.join(", ") : label("structureMap.flow.undefined", "undefined")}
      </div>
      {data.rows.length === 0 ? (
        <div className={styles.empty}>
          {label("structureMap.flow.emptyRule", "This rule has no {{part}} yet.", {
            part: isSource ? label("structureMap.flow.source", "source") : label("structureMap.flow.target", "target"),
          } as any)}
        </div>
      ) : (
        data.rows.map((row) => {
          return (
            <div className={styles.elementRow} key={row.id} style={{ marginLeft: `${row.depth * 12}px` }}>
              {!isSource && <Handle type="target" id={row.id} position={Position.Left} className={styles.rowHandle} />}
              <div className={styles.rowTitle}>
                {row.hasChildren && row.partIndex === 0 && (
                  <button
                    type="button"
                    className={styles.flowToggle}
                    onClick={() => data.onToggleRule(row.rulePath)}
                    aria-label={
                      row.isCollapsed
                        ? label("structureMap.flow.expandRule", "Expand rule {{name}}", { name: row.ruleName })
                        : label("structureMap.flow.collapseRule", "Collapse rule {{name}}", { name: row.ruleName })
                    }
                  >
                    {row.isCollapsed ? "+" : "−"}
                  </button>
                )}
                <span>{row.ruleName}</span>
                {isSource && row.partIndex === 0 && (
                  <button
                    type="button"
                    className={styles.deleteRuleButton}
                    onClick={() => data.onDeleteRule(row.rulePath)}
                    aria-label={label("structureMap.action.deleteRuleFromSource", "Delete rule {{name}}", {
                      name: row.ruleName,
                    })}
                  >
                    ×
                  </button>
                )}
                <small>
                  {isSource ? label("structureMap.flow.read", "Read") : label("structureMap.flow.write", "Write")}{" "}
                  {row.partIndex + 1}
                </small>
              </div>
              <label>
                {label("structureMap.field.context", "Context")}
                <input
                  defaultValue={row.fragment.context ?? ""}
                  onBlur={(event) => update(row, "context", event.target.value)}
                />
              </label>
              <label>
                {label("structureMap.field.element", "Element")}
                <input
                  defaultValue={row.fragment.element ?? ""}
                  placeholder={isSource ? "PID-3" : "identifier[+].value"}
                  onBlur={(event) => update(row, "element", event.target.value)}
                />
              </label>
              {isSource ? (
                <>
                  {row.partIndex === 0 && (
                    <label>
                      {label("structureMap.field.ruleName", "Rule name")}
                      <input
                        defaultValue={row.name}
                        onBlur={(event) => data.onRenameRule(row.rulePath, event.target.value)}
                      />
                    </label>
                  )}
                  <label>
                    {label("structureMap.field.variable", "Variable")}
                    <input
                      defaultValue={row.fragment.variable ?? ""}
                      placeholder="value"
                      onBlur={(event) => update(row, "variable", event.target.value)}
                    />
                  </label>
                  <label>
                    {label("structureMap.field.condition", "Condition (FHIRPath)")}
                    <input
                      defaultValue={row.fragment.condition ?? ""}
                      placeholder="$this.exists()"
                      onBlur={(event) => update(row, "condition", event.target.value)}
                    />
                  </label>
                  {row.partIndex === 0 && (
                    <div className={styles.rowActions}>
                      <button
                        type="button"
                        onClick={() => data.onAddTarget(row.rulePath)}
                        aria-label={label("structureMap.action.addTargetForRule", "Add target for rule {{name}}", {
                          name: row.ruleName,
                        })}
                      >
                        {label("structureMap.action.addTarget", "Add target")}
                      </button>
                      <button
                        type="button"
                        onClick={() => data.onAddChildRule(row.rulePath)}
                        aria-label={label("structureMap.action.addChildRuleToRule", "Add child rule to {{name}}", {
                          name: row.ruleName,
                        })}
                      >
                        {label("structureMap.action.addChildRule", "Add child rule")}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label>
                    {label("structureMap.field.targetVariable", "Target variable")}
                    <input
                      defaultValue={row.fragment.variable ?? ""}
                      placeholder="targetValue"
                      onBlur={(event) => update(row, "variable", event.target.value)}
                    />
                  </label>
                  <label>
                    {label("structureMap.field.contextType", "Context type")}
                    <select
                      defaultValue={row.fragment.contextType ?? "variable"}
                      onChange={(event) => update(row, "contextType", event.target.value)}
                    >
                      <option value="variable">variable</option>
                      <option value="type">type</option>
                    </select>
                  </label>
                </>
              )}
              {isSource && <Handle type="source" id={row.id} position={Position.Right} className={styles.rowHandle} />}
            </div>
          );
        })
      )}
    </div>
  );
}

function Transform({ data }: NodeProps<TransformNode>) {
  const { t } = useTranslation();
  const parameter = getParameterValue(data.target.fragment.parameter?.[0]);
  const label = (key: string, defaultValue: string, options?: JsonRecord) => t(key, { defaultValue, ...options });
  return (
    <div className={styles.transformNode}>
      <Handle type="target" position={Position.Left} className={styles.transformHandle} />
      <button
        type="button"
        className={styles.deleteTransformButton}
        onClick={() => data.onDeleteTarget(data.target)}
        aria-label={label("structureMap.action.deleteTarget", "Delete target {{index}} from {{name}}", {
          index: data.target.partIndex + 1,
          name: data.target.ruleName,
        })}
      >
        ×
      </button>
      <div className={styles.transformTitle}>{label("structureMap.field.transformation", "Transformation")}</div>
      <select
        aria-label={label("structureMap.field.transformation", "Transformation")}
        defaultValue={data.target.fragment.transform ?? "copy"}
        onChange={(event) => data.onUpdate(data.target, { transform: event.target.value })}
      >
        {transforms.map((transform) => (
          <option key={transform} value={transform}>
            {transform}
          </option>
        ))}
      </select>
      <div className={[styles.parameterRow, !parameter.key && styles.parameterRowEmpty].filter(Boolean).join(" ")}>
        <label>
          {label("structureMap.field.parameterType", "Type")}
          <select
            aria-label={label("structureMap.field.parameterType", "Type")}
            defaultValue={parameter.key}
            onChange={(event) =>
              data.onUpdate(data.target, { parameter: event.target.value ? [{ [event.target.value]: "" }] : [] })
            }
          >
            <option value="">{label("structureMap.field.noParameter", "No parameter")}</option>
            {parameterTypes.map((type) => (
              <option key={type.key} value={type.key}>
                {type.label}
              </option>
            ))}
          </select>
        </label>
        {parameter.key && (
          <label>
            {label("structureMap.field.parameterValue", "Value")}
            <input
              key={parameter.key}
              defaultValue={parameter.value}
              placeholder={label("structureMap.field.parameterPlaceholder", "variable or fixed value")}
              onBlur={(event) =>
                data.onUpdate(data.target, {
                  parameter: event.target.value
                    ? [{ [parameter.key]: parameterValue(parameter.key, event.target.value) }]
                    : [],
                })
              }
            />
          </label>
        )}
      </div>
      <Handle type="source" position={Position.Right} className={styles.transformHandle} />
    </div>
  );
}

const nodeTypes = { mappingBox: MappingBox, transform: Transform };

function getRuleAtPath(group: JsonRecord, path: number[]): JsonRecord | undefined {
  let rules = asArray(group.rule);
  let rule: JsonRecord | undefined;
  for (const index of path) {
    rule = rules[index];
    if (!rule) return undefined;
    rules = asArray(rule.rule);
  }
  return rule;
}

interface RuleEntry {
  path: number[];
  rule: JsonRecord;
  label: string;
}
function getRuleEntries(group: JsonRecord): RuleEntry[] {
  const entries: RuleEntry[] = [];
  const visit = (rules: JsonRecord[], parentPath: number[]) =>
    rules.forEach((rule, index) => {
      const path = [...parentPath, index];
      entries.push({ path, rule, label: `${path.map((item) => item + 1).join(".")} · ${rule.name ?? "unnamed"}` });
      visit(asArray(rule.rule), path);
    });
  visit(asArray(group.rule), []);
  return entries;
}
function defaultRule(group: JsonRecord, siblingCount: number, parent?: JsonRecord): JsonRecord {
  const sourceContext = asArray(group.input).find((input) => input.mode === "source")?.name ?? "source";
  return {
    name: parent ? `${parent.name ?? "rule"}-child-${siblingCount + 1}` : `mapping-${siblingCount + 1}`,
    source: [{ context: sourceContext, element: "", variable: "value" }],
  };
}

interface StructureMapFlowEditorProps {
  structureMap: JsonRecord;
  onChange: (structureMap: JsonRecord) => void;
}

export default function StructureMapFlowEditor({ structureMap, onChange }: StructureMapFlowEditorProps) {
  const { t, i18n } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [focusPath, setFocusPath] = useState<number[]>([]);
  const [collapsedTreePaths, setCollapsedTreePaths] = useState<Set<string>>(new Set());
  const [collapsedFlowPaths, setCollapsedFlowPaths] = useState<Set<string>>(new Set());
  const [treeSearch, setTreeSearch] = useState("");
  const [initializedTreeGroup, setInitializedTreeGroup] = useState<number>();
  const label = useCallback(
    (key: string, defaultValue: string, options?: JsonRecord) => t(key, { defaultValue, ...options }),
    [t],
  );
  const group = asArray(structureMap.group)[selectedGroup];
  const focusedRule = group && focusPath.length ? getRuleAtPath(group, focusPath) : undefined;
  const ruleEntries = useMemo(() => getRuleEntries(group ?? {}), [group]);
  const visibleTreeEntries = useMemo(() => {
    const search = treeSearch.trim().toLocaleLowerCase();
    if (search) {
      const matches = ruleEntries.filter((entry) => entry.label.toLocaleLowerCase().includes(search));
      return ruleEntries.filter((entry) => matches.some((match) => isDescendantOf(match.path, entry.path)));
    }
    return ruleEntries.filter((entry) =>
      entry.path.slice(0, -1).every((_, index) => !collapsedTreePaths.has(pathKey(entry.path.slice(0, index + 1)))),
    );
  }, [collapsedTreePaths, ruleEntries, treeSearch]);
  const focusedEntries = useMemo(
    () => (focusPath.length ? ruleEntries.filter((entry) => isDescendantOf(entry.path, focusPath)) : ruleEntries),
    [focusPath, ruleEntries],
  );
  const visibleRules = useMemo(() => {
    if (focusPath.length) return focusedEntries;
    return focusedEntries.filter((entry) =>
      entry.path.slice(0, -1).every((_, index) => !collapsedFlowPaths.has(pathKey(entry.path.slice(0, index + 1)))),
    );
  }, [collapsedFlowPaths, focusPath, focusedEntries]);

  useEffect(() => {
    if (selectedGroup >= asArray(structureMap.group).length) setSelectedGroup(0);
    if (group && focusPath.length && !getRuleAtPath(group, focusPath)) setFocusPath([]);
    if (group && initializedTreeGroup !== selectedGroup) {
      const collapsedPaths = new Set(
        getRuleEntries(group)
          .filter((entry) => asArray(entry.rule.rule).length > 0)
          .map((entry) => pathKey(entry.path)),
      );
      setCollapsedTreePaths(collapsedPaths);
      setCollapsedFlowPaths(collapsedPaths);
      setInitializedTreeGroup(selectedGroup);
    }
  }, [focusPath, group, initializedTreeGroup, selectedGroup, structureMap.group]);
  const updateElement = useCallback(
    (part: MappingPart, row: ElementRow, patch: JsonRecord) => {
      const next = clone(structureMap);
      const fragment = getRuleAtPath(next.group[selectedGroup], row.rulePath)?.[part]?.[row.partIndex];
      if (!fragment) return;
      Object.entries(patch).forEach(([field, value]) => {
        if (value === "" || (Array.isArray(value) && value.length === 0)) delete fragment[field];
        else fragment[field] = value;
      });
      onChange(next);
    },
    [onChange, selectedGroup, structureMap],
  );
  const renameRule = useCallback(
    (path: number[], name: string) => {
      const next = clone(structureMap);
      const rule = getRuleAtPath(next.group[selectedGroup], path);
      if (!rule) return;
      rule.name = name.trim() || "unnamed";
      onChange(next);
    },
    [onChange, selectedGroup, structureMap],
  );
  const addTargetToRule = useCallback(
    (path: number[]) => {
      const next = clone(structureMap);
      const nextGroup = next.group[selectedGroup];
      const rule = getRuleAtPath(nextGroup, path);
      if (!rule) return;
      const targetContext = asArray(nextGroup.input).find((input) => input.mode === "target")?.name ?? "target";
      const sourceVariable = asArray(rule.source)[0]?.variable || "value";
      (rule.target ??= []).push({
        context: targetContext,
        contextType: "variable",
        element: "",
        variable: "targetValue",
        transform: "copy",
        parameter: [{ valueId: sourceVariable }],
      });
      onChange(next);
    },
    [onChange, selectedGroup, structureMap],
  );
  const addChildRule = useCallback(
    (path: number[]) => {
      const next = clone(structureMap);
      const nextGroup = next.group[selectedGroup];
      const parent = getRuleAtPath(nextGroup, path);
      if (!parent) return;
      const rules = (parent.rule ??= []);
      rules.push(defaultRule(nextGroup, rules.length, parent));
      const parentKey = pathKey(path);
      setCollapsedTreePaths((current) => {
        const expanded = new Set(current);
        expanded.delete(parentKey);
        return expanded;
      });
      setCollapsedFlowPaths((current) => {
        const expanded = new Set(current);
        expanded.delete(parentKey);
        return expanded;
      });
      onChange(next);
    },
    [onChange, selectedGroup, structureMap],
  );
  const deleteTarget = useCallback(
    (row: ElementRow) => {
      const next = clone(structureMap);
      const rule = getRuleAtPath(next.group[selectedGroup], row.rulePath);
      if (!rule?.target?.[row.partIndex]) return;
      rule.target.splice(row.partIndex, 1);
      if (!rule.target.length) delete rule.target;
      onChange(next);
    },
    [onChange, selectedGroup, structureMap],
  );
  const deleteRule = useCallback(
    (path: number[]) => {
      const next = clone(structureMap);
      const nextGroup = next.group[selectedGroup];
      if (!nextGroup) return;
      const parent = path.length === 1 ? undefined : getRuleAtPath(nextGroup, path.slice(0, -1));
      const rules = parent ? parent.rule : nextGroup.rule;
      if (!rules?.[path[path.length - 1]]) return;
      rules.splice(path[path.length - 1], 1);
      if (isDescendantOf(focusPath, path)) setFocusPath([]);
      onChange(next);
    },
    [focusPath, onChange, selectedGroup, structureMap],
  );
  const toggleFlowRule = useCallback(
    (path: number[]) =>
      setCollapsedFlowPaths((current) => {
        const next = new Set(current);
        const key = pathKey(path);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );
  const toggleTreeRule = (event: MouseEvent, path: number[]) => {
    event.stopPropagation();
    setCollapsedTreePaths((current) => {
      const next = new Set(current);
      const key = pathKey(path);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const flow = useMemo(() => {
    if (!group) return { nodes: [] as Array<MappingBoxNode | TransformNode>, edges: [] as Edge[] };
    const toRow = (part: MappingPart) =>
      visibleRules.flatMap(({ path, rule }) =>
        asArray(rule[part]).map((fragment, partIndex) => ({
          id: `${part}-${pathKey(path)}-${partIndex}`,
          partIndex,
          rulePath: path,
          ruleName: `${path.map((item) => item + 1).join(".")} · ${rule.name ?? "unnamed"}`,
          name: rule.name ?? "unnamed",
          depth: path.length - (focusPath.length || 1),
          hasChildren: asArray(rule.rule).length > 0,
          isCollapsed: collapsedFlowPaths.has(pathKey(path)),
          fragment: clone(fragment),
        })),
      );
    const sourceRows = toRow("source");
    const targetRows = toRow("target");
    const title = focusedRule
      ? label("structureMap.flow.focus", "Focus: {{name}}", {
          name: focusedRule.name ?? label("structureMap.flow.unnamed", "unnamed"),
        })
      : label("structureMap.flow.root", "{{group}} · root level", {
          group: group.name ?? label("structureMap.flow.group", "Group {{index}}", { index: selectedGroup + 1 }),
        });
    const makeBox = (part: MappingPart, x: number): MappingBoxNode => ({
      id: part,
      type: "mappingBox",
      position: { x, y: 48 },
      draggable: true,
      deletable: false,
      data: {
        part,
        title,
        contexts: asArray(group.input)
          .filter((input) => input.mode === part)
          .map((input) => `${input.name}${input.type ? ` (${input.type})` : ""}`),
        rows: part === "source" ? sourceRows : targetRows,
        onUpdate: updateElement,
        onRenameRule: renameRule,
        onAddTarget: addTargetToRule,
        onAddChildRule: addChildRule,
        onDeleteRule: deleteRule,
        onToggleRule: toggleFlowRule,
      },
    });
    const transformNodes: TransformNode[] = targetRows.map((target, index) => ({
      id: `transform-${pathKey(target.rulePath)}-${target.partIndex}`,
      type: "transform",
      position: { x: 485, y: 95 + index * 185 },
      draggable: true,
      deletable: false,
      data: {
        target,
        onUpdate: (row, patch) => updateElement("target", row, patch),
        onDeleteTarget: deleteTarget,
      },
    }));
    const targetEdges = targetRows.flatMap((target) => {
      const transformId = `transform-${pathKey(target.rulePath)}-${target.partIndex}`;
      const sourceForRule = sourceRows.filter((source) => pathKey(source.rulePath) === pathKey(target.rulePath));
      return [
        ...sourceForRule.map((source) => ({
          id: `source-transform-${source.id}-${target.id}`,
          source: "source",
          sourceHandle: source.id,
          target: transformId,
          markerEnd: { type: MarkerType.ArrowClosed },
          deletable: false,
        })),
        {
          id: `transform-target-${target.id}`,
          source: transformId,
          target: "target",
          targetHandle: target.id,
          markerEnd: { type: MarkerType.ArrowClosed },
          deletable: false,
        },
      ];
    });
    return { nodes: [makeBox("source", 40), ...transformNodes, makeBox("target", 790)], edges: targetEdges };
  }, [
    collapsedFlowPaths,
    focusPath,
    focusedRule,
    group,
    label,
    selectedGroup,
    addChildRule,
    addTargetToRule,
    deleteRule,
    deleteTarget,
    renameRule,
    toggleFlowRule,
    updateElement,
    visibleRules,
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState<MappingBoxNode | TransformNode>(flow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(flow.edges);
  const flowInstanceRef = useRef<ReactFlowInstance<MappingBoxNode | TransformNode, Edge> | null>(null);
  const flowIdentity = useMemo(
    () =>
      JSON.stringify({
        group,
        selectedGroup,
        focusPath,
        collapsedFlowPaths: [...collapsedFlowPaths],
        language: i18n.language,
      }),
    [collapsedFlowPaths, focusPath, group, i18n.language, selectedGroup],
  );
  const flowRef = useRef(flow);
  flowRef.current = flow;
  useEffect(() => {
    setNodes(flowRef.current.nodes);
    setEdges(flowRef.current.edges);
  }, [flowIdentity, setEdges, setNodes]);
  const fitDiagram = useCallback(() => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        flowInstanceRef.current?.fitView({ padding: 0.12, duration: 250, maxZoom: 1 });
      }),
    );
  }, []);
  const focusRule = (path: number[]) => {
    setFocusPath(path);
    fitDiagram();
  };
  const addRule = () => {
    if (!group) return;
    const next = clone(structureMap);
    const nextGroup = next.group[selectedGroup];
    const parent = focusPath.length ? getRuleAtPath(nextGroup, focusPath) : undefined;
    const rules = parent ? (parent.rule ??= []) : (nextGroup.rule ??= []);
    rules.push(defaultRule(nextGroup, rules.length, parent));
    onChange(next);
  };
  const deleteFocusedRule = () => {
    if (focusPath.length) deleteRule(focusPath);
  };

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <Form.Select
          size="sm"
          value={selectedGroup}
          onChange={(event) => {
            setSelectedGroup(Number(event.target.value));
            setFocusPath([]);
            setTreeSearch("");
            setInitializedTreeGroup(undefined);
            setCollapsedFlowPaths(new Set());
          }}
          aria-label={label("structureMap.flow.selectGroup", "Group to edit")}
        >
          {asArray(structureMap.group).map((item, index) => (
            <option key={index} value={index}>
              {item.name ?? label("structureMap.flow.group", "Group {{index}}", { index: index + 1 })}
            </option>
          ))}
        </Form.Select>
        <Button size="sm" variant="primary" onClick={addRule}>
          {focusPath.length
            ? label("structureMap.action.addChildRule", "Add child rule")
            : label("structureMap.action.addRule", "Add rule")}
        </Button>
        {focusPath.length > 0 && (
          <Button size="sm" variant="outline-secondary" onClick={() => focusRule([])}>
            {label("structureMap.action.showGroup", "Show whole group")}
          </Button>
        )}
        {focusPath.length > 0 && (
          <Button size="sm" variant="outline-danger" onClick={deleteFocusedRule}>
            {label("structureMap.action.deleteRule", "Delete rule")}
          </Button>
        )}
        <span>
          {label(
            "structureMap.flow.focusHelp",
            "Focus isolates a rule and its links. Child rules run in their parent context.",
          )}
        </span>
      </div>
      <div className={styles.workspace}>
        <aside className={styles.ruleTree} aria-label={label("structureMap.flow.ruleTree", "Rule tree")}>
          <div className={styles.treeHeading}>{label("structureMap.flow.rulesOfGroup", "Rules of the group")}</div>
          <Form.Control
            size="sm"
            className={styles.treeSearch}
            value={treeSearch}
            onChange={(event) => setTreeSearch(event.target.value)}
            placeholder={label("structureMap.flow.searchRules", "Search rules...")}
            aria-label={label("structureMap.flow.searchRules", "Search rules...")}
          />
          <div className={styles.treeActions}>
            <button
              type="button"
              onClick={() =>
                setCollapsedTreePaths(
                  new Set(
                    getRuleEntries(group ?? {})
                      .filter((entry) => asArray(entry.rule.rule).length > 0)
                      .map((entry) => pathKey(entry.path)),
                  ),
                )
              }
            >
              {label("structureMap.flow.collapseAll", "Collapse all")}
            </button>
            <button type="button" onClick={() => setCollapsedTreePaths(new Set())}>
              {label("structureMap.flow.expandAll", "Expand all")}
            </button>
          </div>
          <button
            type="button"
            className={!focusPath.length ? styles.activeTreeItem : styles.treeItem}
            onClick={() => focusRule([])}
          >
            <span className={styles.treeLabel}>{label("structureMap.flow.overview", "Overview")}</span>
            <span className={styles.treeCount}>{asArray(group?.rule).length}</span>
          </button>
          {visibleTreeEntries.map((entry) => {
            const hasChildren = asArray(entry.rule.rule).length > 0;
            const isCollapsed = collapsedTreePaths.has(pathKey(entry.path));
            return (
              <button
                type="button"
                key={pathKey(entry.path)}
                className={pathKey(entry.path) === pathKey(focusPath) ? styles.activeTreeItem : styles.treeItem}
                style={{ paddingLeft: `${0.7 + entry.path.length * 0.8}rem` }}
                onClick={() => focusRule(entry.path)}
              >
                <span className={styles.treeLabel}>{entry.label}</span>
                {hasChildren && (
                  <span
                    className={styles.treeCollapseAction}
                    onClick={(event) => toggleTreeRule(event, entry.path)}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      isCollapsed
                        ? label("structureMap.flow.expandRule", "Expand rule {{name}}", { name: entry.rule.name })
                        : label("structureMap.flow.collapseRule", "Collapse rule {{name}}", { name: entry.rule.name })
                    }
                  >
                    {isCollapsed
                      ? label("structureMap.action.expand", "Show")
                      : label("structureMap.action.collapse", "Hide")}
                  </span>
                )}
                <span className={styles.treeCount}>{asArray(entry.rule.rule).length}</span>
              </button>
            );
          })}
        </aside>
        <div className={styles.canvas}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onInit={(instance) => {
              flowInstanceRef.current = instance;
            }}
            fitView
            defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
          >
            <Background gap={18} size={1} />
            <Controls />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}
